// Display formatters. All numeric output is destined for .br-tabular-nums spans upstream.

import type { MetricUnit } from "@busright/data-contract";

const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const USD_COMPACT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});
const INT = new Intl.NumberFormat("en-US");

export const formatInt = (n: number): string => INT.format(Math.round(n));

export const formatUsd = (n: number): string => USD.format(n);

export const formatUsdCompact = (n: number): string => USD_COMPACT.format(n);

export const formatPct = (n: number, digits = 1): string => `${n.toFixed(digits)}%`;

export const formatScore = (n: number | null, digits = 1): string =>
  n === null ? "—" : n.toFixed(digits);

export const formatSignedPct = (n: number | null): string => {
  if (n === null) {
    return "—";
  }
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

export const formatSignedNum = (n: number, digits = 1): string => {
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)}`;
};

// Truncate a UUID-ish district id to a stable short label for dense lists.
export const shortId = (id: string): string => id.slice(0, 8);

// Relative-time string from an ISO instant/date, for freshness pills.
const MS_PER_MIN = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MIN;
const MS_PER_DAY = 24 * MS_PER_HOUR;

// Format a numeric MB value with a unit suffix (e.g. 2241 → "2,241 MB").
export const formatMb = (n: number): string => `${INT.format(Math.round(n))} MB`;

// Unit-aware formatter for a dataset cell / metric value, driven by the contract's MetricUnit.
// Keeps "fact vs score" honest: a `score` renders as a bare 0–100 number, money as USD, etc. Null
// is the explicit missing marker (distinct from 0) and always renders as an em dash.
export const formatMetricValue = (
  value: string | number | boolean | null,
  unit?: MetricUnit,
): string => {
  if (value === null) {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "string") {
    return value;
  }
  switch (unit) {
    case "usd":
      return USD.format(value);
    case "percent":
      return formatPct(value);
    case "score":
      return value.toFixed(1);
    case "celsius":
      return `${value.toFixed(1)} °C`;
    case "volt":
      return `${value.toFixed(2)} V`;
    case "milliamp":
      return `${formatInt(value)} mA`;
    case "mb":
      return formatMb(value);
    case "db":
      return `${formatInt(value)} dBm`;
    case "meters":
      return `${formatInt(value)} m`;
    case "mph":
      return `${value.toFixed(1)} mph`;
    case "days":
      return `${formatInt(value)}d`;
    case "date":
      return String(value);
    default:
      return formatInt(value);
  }
};

export const relativeFromNow = (iso: string | null): string => {
  if (!iso) {
    return "unknown";
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return iso;
  }
  const diff = Date.now() - then;
  if (diff < MS_PER_HOUR) {
    return `${Math.max(1, Math.round(diff / MS_PER_MIN))}m ago`;
  }
  if (diff < MS_PER_DAY) {
    return `${Math.round(diff / MS_PER_HOUR)}h ago`;
  }
  return `${Math.round(diff / MS_PER_DAY)}d ago`;
};
